// @owners { team: patient-fulfillment }
import { type ModeType } from '@alto/deliver_api/types/v1/delivery_methods/mode_type';
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  AltoIcon,
  Card,
  Description,
  LgPadding,
  LgSpacing,
  Pressable,
  Row,
  Separator,
  SmSpacing,
  XxsSpacing,
} from '@alto/design-system';
import React, { useCallback, useContext, useEffect } from 'react';
import { type PaymentMethodType } from '~shared/constants';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { getHasNoCopay } from '~shared/features/checkout/selectors/getPaymentBreakdown';
import { getTipAmountWithDefault } from '~shared/features/courier-tips/helpers';
import { getHasAppliedDeliveryFee } from '~shared/features/shipment-fees/selectors/getHasAppliedDeliveryFee';
import { getIsEditingExistingOrder } from '~shared/features/ui/selectors/getCart';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useSelectorShared } from '~shared/store';
import { TipPaymentMethodPicker } from '../../../courier-tip/components/TipPaymentMethodPicker';
import { CheckoutPaymentSelector } from '../../../payment/components/CheckoutPaymentSelector';
import { TipPaymentActionSheet } from './TipPaymentActionSheet';
import { useDeliveryFeeCopy } from './useDeliveryFeeCopy';

export type PaymentMethodCardProps = {
  readonly handleNativePay?: (paymentMethodType: PaymentMethodType) => void;
  readonly disabled?: boolean;
  readonly deliveryMethodMode: ModeType;
};

export const PaymentMethodCard = ({ handleNativePay, deliveryMethodMode, disabled }: PaymentMethodCardProps) => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const isEditingOrder = useSelectorShared(getIsEditingExistingOrder);
  const order = useSelectorShared(getOrder);
  const hasNoCopay = useSelectorShared(getHasNoCopay);
  const { shipment_payment_methods } = order;
  const tipAmount = getTipAmountWithDefault(order.tip_amount);
  const hasDeliveryFeeAmount = useSelectorShared(getHasAppliedDeliveryFee);
  const isCourierMethod = deliveryMethodMode === 'COURIER';
  const isTipAndFeePaymentMethodPickerHidden = (!isCourierMethod || !tipAmount) && !hasDeliveryFeeAmount;
  const isPaymentMethodPickerHidden = hasNoCopay && isTipAndFeePaymentMethodPickerHidden;
  const { trackPageView } = useAnalytics();
  const { label } = useDeliveryFeeCopy();

  useEffect(() => {
    trackPageView({
      event: EVENTS.PAYMENT_METHOD_RENDERED,
      params: {
        origin: isEditingOrder ? 'edit delivery' : 'checkout',
        has_medication_payment: !hasNoCopay,
        has_non_hsa_or_fsa_payment: !isTipAndFeePaymentMethodPickerHidden,
      },
    });
  }, [hasNoCopay, isEditingOrder, isTipAndFeePaymentMethodPickerHidden, trackPageView]);

  const handleTipAndFeePaymentPress = useCallback(() => {
    setActiveActionSheet(<TipPaymentActionSheet />);
  }, [setActiveActionSheet]);

  if (isPaymentMethodPickerHidden) {
    return null;
  }

  return (
    <Card title="Payment method">
      {hasNoCopay ? null : (
        <>
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            {isTipAndFeePaymentMethodPickerHidden ? null : (
              <Description
                color={COLORS.TEXT_COLORS.PRIMARY}
                fontFamily="semibold"
              >
                Medication payment
              </Description>
            )}
            <CheckoutPaymentSelector
              disabled={disabled}
              handleNativePay={handleNativePay}
              shipmentPaymentMethods={shipment_payment_methods}
              hasNoCopay={hasNoCopay}
            />
          </LgPadding>
        </>
      )}
      {isTipAndFeePaymentMethodPickerHidden ? null : (
        <>
          <Separator />
          <LgSpacing />
          <LgPadding topPadding={SPACING.STATIC.NONE}>
            <Pressable onPress={handleTipAndFeePaymentPress}>
              <Row centerVertically>
                <Description
                  color={COLORS.TEXT_COLORS.PRIMARY}
                  fontFamily="semibold"
                >
                  {label}
                </Description>
                <XxsSpacing />
                <AltoIcon name="info-small" />
              </Row>
            </Pressable>
            <SmSpacing />
            <TipPaymentMethodPicker handleNativePay={handleNativePay} />
          </LgPadding>
        </>
      )}
    </Card>
  );
};
